import React, { useState } from 'react';

const UploadPopup = ({ jobId, onClose, onUploadSuccess }) => {
  const [selectedFile, setSelectedFile] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    stage: 'preparing',
    message: 'Preparing to upload...'
  });

  // New state to hold duplicates returned from backend
  const [duplicates, setDuplicates] = useState([]);

  const progressMessages = {
    preparing: 'Preparing files for upload...',
    uploading: 'Uploading resumes to server...',
    scoring: 'Calculating match scores...',
    finalizing: 'Finalizing results...'
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFile(files);
    setError('');
    // reset any previous duplicate info when selecting new files
    setDuplicates([]);
  };

  const updateProgress = (stage) => {
    setUploadProgress({
      stage,
      message: progressMessages[stage]
    });
  };

  const handleUpload = async () => {
    if (selectedFile.length === 0) {
      setError('Please select at least one PDF file');
      return;
    }

    setIsUploading(true);
    updateProgress('preparing');

    try {
      const formData = new FormData();
      selectedFile.forEach((file, index) => {
        formData.append(`file${index}`, file);
      });
      formData.append('job_id', jobId);

      updateProgress('uploading');

      const response = await fetch('/api/handle_resume_upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => 'File upload failed.');
        throw new Error(errText || 'File upload failed.');
      }

      updateProgress('scoring');
      // small UX delay to show progress stages
      await new Promise(resolve => setTimeout(resolve, 500));

      updateProgress('finalizing');
      await new Promise(resolve => setTimeout(resolve, 500));

      const result = await response.json();

      // set duplicates to show in UI
      const dupList = result.duplicates || [];
      setDuplicates(dupList)

      console.log(duplicates)

      // call parent callback with successful new candidates
      onUploadSuccess(result.newCandidates || []);

      setShowSuccess(true);

      // if there are no duplicates we auto-close after 3s, otherwise keep open so user can inspect
      if (!dupList.length) {
        setTimeout(() => {
          onClose();
        }, 3000);
      }
    } catch (err) {
      setError('An error occurred while uploading the file');
      console.error('Error uploading files:', err);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      {!showSuccess && (
        <div className="popup-overlay-res">
          <div className="popup-container">
            <h2>Upload Resumes</h2>
            <p>Please select files to upload.</p>

            {isUploading ? (
              <div className="modern-loader">
                <div className="loader-animation">
                  <div className="loader-circle"></div>
                  <div className="loader-circle"></div>
                  <div className="loader-circle"></div>
                </div>
                <div className="progress-message">
                  {uploadProgress.message}
                </div>
                <div className="progress-stages">
                  {Object.keys(progressMessages).map((stage) => (
                    <div 
                      key={stage}
                      className={`progress-stage ${uploadProgress.stage === stage ? 'active' : ''}`}
                    >
                      {progressMessages[stage]}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <input
                  type="file"
                  className="file-input-res"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.txt"
                  multiple
                />
                {error && <p className="error-message">{error}</p>}
                {selectedFile.length > 0 && (
                  <div className="selected-files">
                    <h3>Selected Files:</h3>
                    <ul>
                      {selectedFile.map((file, index) => (
                        <li key={index}>{file.name}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* If previous upload returned duplicates, show a preview here before next upload */}
                {duplicates.length > 0 && (
                  <div className="duplicate-preview">
                    <h4>Previously detected duplicates (from last upload):</h4>
                    <ul>
                      {duplicates.map((d, i) => (
                        <li key={i}>
                          <strong>{d.uploaded_filename || 'unknown'}</strong>
                          {d.reasons && d.reasons.length > 0 && (
                            <span> — {d.reasons.join(', ')}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}

            <div className="popup-buttons">
              <button 
                className="popup-button cancel" 
                onClick={onClose}
                disabled={isUploading}
              >
                Cancel
              </button>
              <button 
                className={`popup-button upload ${isUploading ? 'uploading' : ''}`}
                onClick={handleUpload}
                disabled={isUploading || selectedFile.length === 0}
              >
                {isUploading ? (
                  <>
                    <span className="upload-spinner"></span>
                    Uploading...
                  </>
                ) : (
                  'Upload CV'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccess && (
        <div className="popup-overlay">
          <div className="popup-success-container">
            <div className="success-animation">
              <svg className="checkmark" viewBox="0 0 52 52">
                <circle className="checkmark-circle" cx="26" cy="26" r="25" fill="none"/>
                <path className="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
              </svg>
            </div>
            <h2>Success!</h2>
            <p>Resumes processed successfully</p>
            <div className="success-details">
              <p>{selectedFile.length} resume(s) analyzed</p>
            </div>

            {/* SHOW DUPLICATES DETAILS IN SUCCESS MODAL */}
            {duplicates.length > 0 ? (
              <div className="duplicates-details">
                <h3>Duplicate File(s) found</h3>
                <p>The following files were detected as duplicates and were not uploaded:</p>
                <ul>
                  {duplicates.map((d, i) => (
                    <li key={i} className="duplicate-item">
                      <div className="dup-filename"><strong>{d.uploaded_filename || 'unknown'}</strong></div>
                    </li>
                  ))}
                </ul>

                <div className="popup-buttons">
                  <button className="popup-button close" onClick={onClose}>Close</button>
                </div>
              </div>
            ) : (
              // No duplicates — keep the small success view
              <div className="popup-buttons">
                <button className="popup-button close" onClick={onClose}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default UploadPopup;
